import { EsignFormType, SigningService } from '@buyerflo/core/schemas/esign'
import { useMutation } from '@tanstack/react-query'
import Modal, { dontCloseOnBackgroundClick } from 'components/modal'
import { usePurchaserContext } from 'purchasers/purchaserProvider'
import { useEffect, useRef, useState } from 'react'
import Button from 'components/button'
import { Dialog, Text, View, XStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'

interface IframeModalProps {
    title: string
    url: string | undefined
    onClose: () => void
    signingService?: SigningService
    purchaserId?: string
    formType?: EsignFormType | 'generic'
    documentId?: string
}

const IframeModal = ({
    title,
    url,
    onClose: onCloseProp,
    signingService = 'annature',
    purchaserId,
    formType,
    documentId,
}: IframeModalProps) => {
    const trpc = useTRPC()
    const purchaserContext = usePurchaserContext()
    const setPurchaser = purchaserContext?.setPurchaser
    const refreshPurchaser = purchaserContext?.refreshPurchaser
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const [isSaving, setIsSaving] = useState(false)
    const [saveError, setSaveError] = useState<string>()
    const sendFormMutation = useMutation(trpc.esign.sendForm.mutationOptions())

    const onClose = () => {
        // DocuSign sends from inside its own embedded view, so the status flips
        // via the Connect webhook. Refresh on close so the stepper reflects it.
        if (signingService !== 'annature') {
            void refreshPurchaser?.()
        }
        onCloseProp()
        setSaveError(undefined)
        setIsSaving(false)
    }

    // When the agent sends/cancels in DocuSign's embedded sender view, DocuSign
    // redirects the iframe to our public /docusign/sender-return page, which posts
    // this message. Close the modal in response (which refreshes the form status).
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose
    useEffect(() => {
        if (signingService === 'annature') return
        const handler = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return
            if (event.data?.source === 'docusign-sender-return') onCloseRef.current()
        }
        window.addEventListener('message', handler)
        return () => window.removeEventListener('message', handler)
    }, [signingService])

    const waitForAnnatureSave = () => {
        return new Promise<void>((resolve, reject) => {
            const handler = (event: MessageEvent) => {
                if (event.data.event === 'annatureSaveSuccess') {
                    window.removeEventListener('message', handler)
                    resolve()
                }
                if (event.data.event === 'annatureSaveError') {
                    window.removeEventListener('message', handler)
                    reject(new Error(event.data.message || 'Save failed'))
                }
            }
            window.addEventListener('message', handler)
            iframeRef.current?.contentWindow?.postMessage('annatureSave', '*')
        })
    }

    const handleSaveAndClose = async () => {
        setIsSaving(true)
        try {
            setSaveError(undefined)
            await waitForAnnatureSave()
            onClose()
        } catch (error: any) {
            setSaveError(error.message || 'Save failed')
        } finally {
            setIsSaving(false)
        }
    }

    const handleSaveAndSend = async () => {
        setIsSaving(true)
        try {
            setSaveError(undefined)
            await waitForAnnatureSave()
            if (purchaserId && formType) {
                const { updatedPurchaser } = await sendFormMutation.mutateAsync({
                    purchaserId,
                    formType,
                    formId: documentId,
                })
                setPurchaser?.(updatedPurchaser)
            }
            onClose()
        } catch (error: any) {
            setSaveError(error.message || 'Error saving form')
        } finally {
            setIsSaving(false)
        }
    }

    const handleCancel = () => {
        onClose()
    }

    const isAnnature = signingService === 'annature'

    return (
        <Modal
            open={url !== undefined}
            onOpenChange={(open) => { if (!open) onClose() }}
            width="90vw"
            maxWidth={1200}
            height="90vh"
        >
            <Dialog.Title>{title}</Dialog.Title>
            <Dialog.Description asChild>
                <View flex={1}>
                    <iframe
                        ref={iframeRef}
                        src={url}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            minHeight: '70vh',
                        }}
                    />
                </View>
            </Dialog.Description>
            {saveError && (
                <View
                    paddingHorizontal="$4"
                    paddingVertical="$2"
                    backgroundColor="$red3"
                    borderTopWidth={1}
                    borderColor="$red7"
                >
                    <Text color="$red11">Error: {saveError}</Text>
                </View>
            )}
            <XStack gap="$2" flexDirection="row-reverse">
                {isAnnature ? (
                    <>
                        <Button
                            onPress={handleSaveAndSend}
                            disabled={isSaving}
                        >
                            Save & Send
                        </Button>
                        <Button
                            onPress={handleSaveAndClose}
                            disabled={isSaving}
                        >
                            Save & Close
                        </Button>
                        <View flex={1} />
                        <Button
                            variant="outlined"
                            onPress={handleCancel}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                    </>
                ) : (
                    <Button onPress={onClose}>Close</Button>
                )}
            </XStack>
        </Modal>
    )
}

export default IframeModal
